import { Box, Checkbox, TextField, Typography } from '@mui/material'
import type { SendChannel } from '@/services/notificationService'
import SegmentCounter from './SegmentCounter'
import { CHANNEL_ICONS, channelKind, charLimit } from './channelKinds'
import type { ChannelDraft } from './sendContent'
import {
  collapseWhitespace,
  renderWhatsappBody,
  WHATSAPP_TEMPLATE,
} from './whatsappText'

type Props = {
  channel: SendChannel
  ticked: boolean
  onTick: (ticked: boolean) => void
  draft: ChannelDraft
  onDraft: (next: ChannelDraft) => void
  // the WhatsApp template's customer_name: the consumer's name, or a marker
  // when the audience is not one consumer
  customerName?: string
  // the alert editor picks channels elsewhere, so it hides the tick box
  showTick?: boolean
  // shown in an empty body, e.g. which text such a channel sends instead
  bodyPlaceholder?: string
  rows?: number
  onFocus: () => void
  inputRef: (el: HTMLTextAreaElement | null) => void
}

const fixedLine = {
  color: 'text.secondary',
  fontSize: 13,
  whiteSpace: 'pre-line',
  px: 0.5,
} as const

const ChannelPanel = ({
  channel,
  ticked,
  onTick,
  draft,
  onDraft,
  customerName,
  showTick = true,
  bodyPlaceholder,
  rows,
  onFocus,
  inputRef,
}: Props) => {
  const kind = channelKind(channel)
  const icon = CHANNEL_ICONS[kind]
  const collapsed = collapseWhitespace(draft.body)
  const limit = charLimit(kind)
  return (
    <Box
      data-testid={`panel-${channel.channelId}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        opacity: ticked ? 1 : 0.6,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && (
          <Box
            component="img"
            src={icon}
            alt=""
            sx={theme => ({
              width: 22,
              height: 22,
              filter:
                theme.palette.mode === 'dark'
                  ? 'brightness(0) invert(1)'
                  : 'none',
            })}
          />
        )}
        <Typography sx={{ fontSize: 14, flexGrow: 1 }}>
          {channel.name}
        </Typography>
        {showTick && (
          <Checkbox
            className="nodrag"
            size="small"
            checked={ticked}
            onChange={e => onTick(e.target.checked)}
            slotProps={{ input: { 'aria-label': channel.name } }}
          />
        )}
      </Box>
      {kind === 'email' && (
        <TextField
          className="nodrag nowheel"
          size="small"
          placeholder="נושא המייל"
          disabled={!ticked}
          value={draft.subject}
          onChange={e => onDraft({ ...draft, subject: e.target.value })}
          slotProps={{
            htmlInput: { dir: 'rtl', 'aria-label': 'נושא המייל' },
          }}
        />
      )}
      <Box sx={{ position: 'relative' }}>
        <TextField
          className="nodrag nowheel"
          multiline
          fullWidth
          rows={rows ?? (kind === 'whatsapp' ? 5 : 10)}
          placeholder={bodyPlaceholder}
          disabled={!ticked}
          value={draft.body}
          onChange={e => onDraft({ ...draft, body: e.target.value })}
          onFocus={onFocus}
          inputRef={inputRef}
          slotProps={{
            htmlInput: {
              dir: 'rtl',
              ...(limit === null ? {} : { maxLength: limit }),
              'aria-label': `הודעה (${channel.name})`,
              style: { paddingBottom: 22 },
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            insetInlineEnd: 12,
            bottom: 8,
            color:
              limit !== null && draft.body.length > limit
                ? 'error.main'
                : 'text.secondary',
            pointerEvents: 'none',
          }}
        >
          {limit === null
            ? `${draft.body.length}`
            : `${draft.body.length}/${limit}`}
        </Typography>
      </Box>
      {kind === 'whatsapp' && (
        <>
          {collapsed !== draft.body && (
            <Typography
              data-testid="whatsapp-collapsed"
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {`WhatsApp שולח שורה אחת. ההודעה תישלח כך: ${collapsed}`}
            </Typography>
          )}
          <Box
            data-testid="whatsapp-preview"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              data-testid="whatsapp-header"
              sx={{ fontWeight: 700, fontSize: 13 }}
            >
              {WHATSAPP_TEMPLATE.header}
            </Typography>
            <Typography data-testid="whatsapp-body" sx={fixedLine}>
              {renderWhatsappBody({ customerName, message: draft.body })}
            </Typography>
          </Box>
        </>
      )}
      {kind === 'sms' && ticked && <SegmentCounter body={draft.body} />}
    </Box>
  )
}

export default ChannelPanel
